class ProductDetailVariation extends HTMLElement {
  constructor() {
    super();
    this.productVariants = this.getProductVariants();
    this.sectionId = `shopify-section-${this.getAttribute("section-id")}`;
    this.form = this.querySelector("form");
  }

  connectedCallback() {
    this.handleVariantChange();
  }

  getProductVariants() {
    try {
      const scriptElement = this.querySelector(
        'script[type="application/json"]'
      );
      if (!scriptElement) {
        console.error("Product variants script not found");
        return [];
      }
      const productVariants = scriptElement.textContent;
      return JSON.parse(productVariants);
    } catch (error) {
      console.error("Error parsing product variants", error);
      return [];
    }
  }

  handleVariantChange() {
    this.addEventListener("change", this.handleFormData.bind(this));
  }

  handleFormData() {
    const formDataArray = [];
    const radioButtons = this.querySelectorAll('input[type="radio"]:checked');

    radioButtons.forEach((input) => {
      if (!input.name.startsWith("variant-group-")) {
        formDataArray.push(input.value);
      }
    });

    const result = this.productVariants.find((variant) => {
      return formDataArray.every(
        (value, index) => variant[`option${index + 1}`] === value
      );
    });

    // Dispatch variant:change event before rerendering (like theme.js does)
    if (result) {
      const selectedOptions = [];
      if (result.option1)
        selectedOptions.push({ name: "Option1", value: result.option1 });
      if (result.option2)
        selectedOptions.push({ name: "Option2", value: result.option2 });
      if (result.option3)
        selectedOptions.push({ name: "Option3", value: result.option3 });

      this.dispatchEvent(
        new CustomEvent("variant:change", {
          bubbles: true,
          detail: {
            variant: {
              id: result.id,
              title: result.title,
              selected_options: selectedOptions,
              option1: result.option1,
              option2: result.option2,
              option3: result.option3,
            },
          },
        })
      );
    }

    this.updateUrlWithVariant(result);
    this.rerenderContent(result);
  }

  async rerenderContent(variant) {
    if (!variant) return;
    const currentUrl = new URL(window.location.href);
    const response = await fetch(`${currentUrl.toString()}`);
    const html = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const newSection = doc.querySelector(`#${this.sectionId}`);

    if (newSection) {
      const currentSection = document.getElementById(this.sectionId);
      this.mergeSection(currentSection, newSection);
    }
  }

  mergeSection(currentSection, newSection) {
    // Preserve elements that should not be re-rendered
    const preservedElements = currentSection.querySelectorAll(
      "[data-preserve-on-rerender]"
    );
    const preservedMap = new Map();

    preservedElements.forEach((el) => {
      const id = el.getAttribute("id") || el.getAttribute("data-preserve-id");
      if (id) {
        preservedMap.set(id, el.cloneNode(true));
      }
    });

    // Replace all children
    currentSection.replaceChildren(...newSection.children);

    // Restore preserved elements
    preservedMap.forEach((preservedEl, id) => {
      const targetEl = currentSection.querySelector(
        `[id="${id}"], [data-preserve-id="${id}"]`
      );
      if (targetEl && targetEl.parentNode) {
        targetEl.parentNode.replaceChild(preservedEl, targetEl);
      }
    });
  }

  updateUrlWithVariant(variant) {
    if (variant && variant.id) {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set("variant", variant.id);
      window.history.replaceState(
        { path: currentUrl.toString() },
        "",
        currentUrl.toString()
      );
    }
  }
}

if (!customElements.get("product-detail-variation")) {
  customElements.define("product-detail-variation", ProductDetailVariation);
}

/**
 * Product detail variant group
 */
class ProductDetailVariantGroup extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  init() {
    const selectElement = this.querySelector("select");
    if (selectElement) {
      selectElement.addEventListener("change", (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.updateVariantItem(event.target.value);
      });
    }

    if (this.querySelector('input[type="radio"]')) {
      this.querySelectorAll('input[type="radio"]').forEach((input) => {
        input.addEventListener("change", (event) => {
          if (event.target.name.startsWith("variant-group-")) {
            event.preventDefault();
            event.stopPropagation();
            this.updateVariantItem(event.target.value);
          }
        });
      });
    }
  }

  updateVariantItem(group) {
    const parentElement = this.closest(".kc-product-detail-variant-picker");
    if (parentElement) {
      const groupTitle = parentElement.querySelector(
        ".variant-list-title-group"
      );
      if (groupTitle) {
        const titleValue = groupTitle.querySelector(
          ".variant-list-title-value"
        );
        if (titleValue) {
          titleValue.textContent = group;
        }
      }

      parentElement.querySelectorAll(".variant-color").forEach((item) => {
        if (item.dataset.group === group) {
          item.removeAttribute("hidden");
        } else {
          item.setAttribute("hidden", "");
        }
      });
    }
  }
}

if (!customElements.get("product-detail-variant-group")) {
  customElements.define(
    "product-detail-variant-group",
    ProductDetailVariantGroup
  );
}

/**
 * Product detail product related
 */
class ProductDetailProductRelated extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.handleRelatedChange();
  }

  handleRelatedChange() {
    const selectElement = this.querySelector("select");
    if (selectElement) {
      selectElement.addEventListener("change", (event) => {
        const url = event.target.value;
        window.location.href = url;
      });
    }
  }
}

if (!customElements.get("product-detail-product-related")) {
  customElements.define(
    "product-detail-product-related",
    ProductDetailProductRelated
  );
}
